/**
 * Custom hook for AttributeMapping component logic
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getDefaultMappings } from '../data-layer/mockData';
import {
  type AttributeMappingConfig,
  type GenericAttributeMapping,
  type UseAttributeMappingReturn,
  type ValidationResult,
} from '../data-layer/types';
import {
  generateMappingId,
  getAvailableSaviyntFields,
  hasMappingsChanged,
  validateExternalAppFields,
  validateRequiredFields,
} from '../utils/attributeMappingUtils';

const useAttributeMapping = (
  config: AttributeMappingConfig,
  initialMappings?: GenericAttributeMapping[],
  onMappingsChange?: (mappings: GenericAttributeMapping[]) => void,
  mandatoryMappings?: GenericAttributeMapping[]
): UseAttributeMappingReturn => {
  // Combine mandatory mappings with initial mappings
  const combinedInitialMappings = useMemo(() => {
    const mandatory =
      mandatoryMappings?.map((m) => ({ ...m, isMandatory: true })) ?? [];
    const initial = initialMappings ?? getDefaultMappings();

    return mandatory.length > 0 ? [...mandatory, ...initial] : initial;
  }, [mandatoryMappings, initialMappings]);

  const [mappings, setMappings] = useState<GenericAttributeMapping[]>(
    combinedInitialMappings
  );
  const [isDirty, setIsDirty] = useState(false);
  const [initialMappingsState] = useState<GenericAttributeMapping[]>(
    combinedInitialMappings
  );

  // Check if mappings have changed from initial state
  useEffect(() => {
    const hasChanged = hasMappingsChanged(mappings, initialMappingsState);

    setIsDirty(hasChanged);
  }, [mappings, initialMappingsState]);

  // Separate effect for onMappingsChange callback
  useEffect(() => {
    if (onMappingsChange) {
      onMappingsChange(mappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings]);

  // Get available Saviynt fields
  const availableSaviyntFields = useMemo(
    () => getAvailableSaviyntFields(mappings, config.saviyntApp.fields),
    [mappings, config.saviyntApp.fields]
  );

  // Check if we can add more rows
  const canAddMoreRows = useMemo(() => {
    const totalSaviyntFields = config.saviyntApp.fields.length;

    return mappings.length < totalSaviyntFields;
  }, [mappings.length, config.saviyntApp.fields.length]);

  // Add new mapping
  const handleAddMapping = useCallback(() => {
    if (!canAddMoreRows) {
      return;
    }

    const newMapping: GenericAttributeMapping = {
      id: generateMappingId(),
      saviyntField: '', // No auto-selection
      externalAppField: '',
      saviyntValue: '',
      externalAppValue: '',
    };

    setMappings((prev) => [...prev, newMapping]);
  }, [canAddMoreRows]);

  // Delete mapping (ensure at least 1 row remains)
  const handleDeleteMapping = useCallback((idToDelete: string) => {
    setMappings((prev) => {
      if (prev.length <= 1) {
        return prev; // Cannot delete if only 1 row remains
      }

      return prev.filter((mapping) => mapping.id !== idToDelete);
    });
  }, []);

  // Update mapping
  const handleUpdateMapping = useCallback(
    (id: string, field: string, value: string) => {
      setMappings((prev) =>
        prev.map((mapping) =>
          mapping.id === id ? { ...mapping, [field]: value } : mapping
        )
      );
    },
    []
  );

  // Validate mappings
  const validateMappings = useCallback((): ValidationResult => {
    // Check required fields
    const requiredValidation = validateRequiredFields(mappings);

    if (!requiredValidation.isValid) {
      return requiredValidation;
    }

    // Check external app field uniqueness
    const uniquenessValidation = validateExternalAppFields(mappings);

    if (!uniquenessValidation.isValid) {
      return uniquenessValidation;
    }

    return { isValid: true, errors: [] };
  }, [mappings]);

  return {
    mappings,
    setMappings,
    isDirty,
    canAddMoreRows,
    availableSaviyntFields,
    handleAddMapping,
    handleDeleteMapping,
    handleUpdateMapping,
    validateMappings,
  };
};

export default useAttributeMapping;
